import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { Badge, Button, Card } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { formatCurrency } from "@/components/deals/dealFormat";
import { escrowApi } from "@/api/escrow";
import { extractErrorMessage } from "@/api/client";
import { ApproveEscrowModal } from "@/components/escrow/ApproveEscrowModal";
import { EscrowLifecycleIndicator } from "@/components/escrow/EscrowLifecycleIndicator";
import { EscrowLiveBadge } from "@/components/escrow/EscrowLiveBadge";
import {
  ESCROW_PERMISSIONS,
  ESCROW_STATUS_LABEL,
  escrowStatusBadge,
} from "@/types/escrow";

const TERMINAL_STATUSES = ["Closed", "Cancelled", "Archived"];
const WEBHOOK_STALE_MS = 30 * 60 * 1000;

function formatFee(amount: number, currency: string | null): string {
  const code = currency ?? "USD";
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: code,
    minimumFractionDigits: 2,
  }).format(amount);
  return `${formatted} ${code} (funder pays all)`;
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <h2 className="font-semibold text-onyx mb-space-3">{title}</h2>
      {children}
    </Card>
  );
}

/** Renders a resolved user display name; falls back to "Unknown user" when an
 * id exists but couldn't be resolved (deleted account). Never shows a raw GUID
 * inline — the GUID is only a hover tooltip. */
function UserName({ name, id }: { name: string | null; id: string | null }) {
  if (name) {
    return (
      <span title={id ?? undefined}>{name}</span>
    );
  }
  if (id) {
    return <span title={id} className="text-gray-mid">Unknown user</span>;
  }
  return <>—</>;
}

function DefRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-space-4 py-space-1 border-b border-border-light last:border-0">
      <dt className="text-small text-gray-mid">{label}</dt>
      <dd className="text-small text-onyx text-right">{value}</dd>
    </div>
  );
}

export function EscrowDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const fmt = useDateFormatter();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canApprove = hasPermission(ESCROW_PERMISSIONS.Approve);
  const canDraft = hasPermission(ESCROW_PERMISSIONS.Draft);

  const [approveOpen, setApproveOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [webhookBannerDismissed, setWebhookBannerDismissed] = useState(false);

  const query = useQuery({
    queryKey: ["escrow", "detail", id],
    queryFn: () => escrowApi.get(id),
    enabled: Boolean(id),
  });
  const escrow = query.data ?? null;

  const refreshMutation = useMutation({
    mutationFn: () => escrowApi.refresh(id),
    onSuccess: (data) => {
      toast.success("Synced from Escrow.com.");
      queryClient.setQueryData(["escrow", "detail", id], data);
      queryClient.invalidateQueries({ queryKey: ["escrow", "list"] });
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  const discardMutation = useMutation({
    mutationFn: () => escrowApi.discard(id),
    onSuccess: () => {
      toast.success("Draft discarded.");
      queryClient.invalidateQueries({ queryKey: ["escrow"] });
      navigate("/escrow");
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  const archiveMutation = useMutation({
    mutationFn: () => escrowApi.archive(id),
    onSuccess: () => {
      toast.success("Escrow transaction archived.");
      setArchiveOpen(false);
      queryClient.invalidateQueries({ queryKey: ["escrow"] });
      void query.refetch();
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  if (query.isLoading) {
    return <div className="p-space-4 text-small text-gray-mid">Loading…</div>;
  }
  if (!escrow) {
    return <div className="p-space-4 text-small text-gray-mid">Escrow transaction not found.</div>;
  }

  const isPending = escrow.status === "PendingAdminApproval";
  const inspectionDays = Math.round((escrow.inspectionPeriodSeconds / 86_400) * 10) / 10;

  const showWebhookWarning =
    !webhookBannerDismissed &&
    !TERMINAL_STATUSES.includes(escrow.status) &&
    escrow.events.length === 0 &&
    escrow.approvedAt != null &&
    Date.now() - new Date(escrow.approvedAt).getTime() > WEBHOOK_STALE_MS;

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Escrow"
        title={`${formatCurrency(escrow.amount)} ${escrow.currency}`}
        description={escrow.itemDescription}
        actions={
          <div className="flex items-center gap-space-2 flex-wrap">
            <EscrowLiveBadge isLive={escrow.isLive} />
            <Badge status={escrowStatusBadge(escrow.status)}>{ESCROW_STATUS_LABEL[escrow.status]}</Badge>
            {isPending && canApprove && (
              <Button size="sm" onClick={() => setApproveOpen(true)}>
                Approve
              </Button>
            )}
            {isPending && canDraft && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => discardMutation.mutate()}
                loading={discardMutation.isPending}
              >
                Discard
              </Button>
            )}
            {escrow.status !== "Archived" && canApprove && (
              <Button size="sm" variant="tertiary" onClick={() => setArchiveOpen(true)}>
                Archive
              </Button>
            )}
            <Button
              size="sm"
              variant="tertiary"
              onClick={() => refreshMutation.mutate()}
              loading={refreshMutation.isPending}
              disabled={isPending}
              title={
                isPending
                  ? "Nothing to refresh yet — this draft hasn't been sent to Escrow.com."
                  : "Pull the latest state from Escrow.com"
              }
            >
              <RefreshCw size={14} aria-hidden /> Refresh
            </Button>
            {escrow.externalUrl ? (
              <a
                href={escrow.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-caption text-gold-shadow hover:text-gold-highlight inline-flex items-center"
              >
                View on Escrow.com
              </a>
            ) : (
              <span
                className="text-caption text-gray-mid inline-flex items-center cursor-not-allowed"
                title="Available once the transaction is sent to Escrow.com."
              >
                View on Escrow.com
              </span>
            )}
          </div>
        }
      />

      {showWebhookWarning && (
        <div
          role="alert"
          className="flex items-start justify-between gap-space-3 rounded-lg border border-status-warning bg-status-warning/10 px-space-3 py-space-2"
        >
          <p className="text-small text-onyx m-0">
            Awaiting webhook events from Escrow.com. Click <span className="font-semibold">Refresh</span> to
            manually sync, or contact engineering if this persists.
          </p>
          <button
            type="button"
            onClick={() => setWebhookBannerDismissed(true)}
            className="text-caption text-gray-mid hover:text-onyx shrink-0"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      <Card>
        <EscrowLifecycleIndicator lifecycle={escrow.lifecycle} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-4">
        <SectionCard title="Transaction">
          <dl>
            <DefRow label="Status" value={ESCROW_STATUS_LABEL[escrow.status]} />
            <DefRow label="Amount" value={`${formatCurrency(escrow.amount)} ${escrow.currency}`} />
            <DefRow label="Description" value={escrow.itemDescription} />
            <DefRow label="Inspection period" value={`${inspectionDays} day(s)`} />
            <DefRow label="Escrow.com ID" value={escrow.escrowComTransactionId ?? "—"} />
            <DefRow
              label="Deal"
              value={
                <Link to={`/pipeline/${escrow.dealId}`} className="text-gold-shadow hover:text-gold-highlight">
                  {escrow.dealNumber ?? escrow.dealId.slice(0, 8)}
                  {escrow.businessName ? ` — ${escrow.businessName}` : ""}
                </Link>
              }
            />
          </dl>
        </SectionCard>

        <SectionCard title="Parties & fees">
          <dl>
            <DefRow
              label="Funds from"
              value={
                escrow.buyerEmail ? (
                  <span className="inline-flex flex-col items-end">
                    <span className="break-all">{escrow.buyerEmail}</span>
                    {escrow.buyerEmailOverridden && escrow.canonicalBuyerEmailAtDraft && (
                      <span className="text-caption text-gray-mid break-all">
                        Overridden from {escrow.canonicalBuyerEmailAtDraft}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-status-error">No borrower email set</span>
                )
              }
            />
            <DefRow label="Recipient" value="Phoenix Capital" />
            <DefRow label="Disbursement" value="100% to Phoenix Capital" />
            <DefRow
              label="Escrow.com fees"
              value={
                escrow.escrowComFeeAmount != null
                  ? formatFee(escrow.escrowComFeeAmount, escrow.escrowComFeeCurrency)
                  : "Paid by funder"
              }
            />
          </dl>
        </SectionCard>

        <SectionCard title="Audit">
          <dl>
            <DefRow label="Drafted by" value={<UserName name={escrow.draftedByUserName} id={escrow.draftedByUserId} />} />
            <DefRow label="Drafted at" value={escrow.draftedAt ? fmt.dateTime(escrow.draftedAt) : "—"} />
            <DefRow label="Approved by" value={<UserName name={escrow.approvedByUserName} id={escrow.approvedByUserId} />} />
            <DefRow label="Approved at" value={escrow.approvedAt ? fmt.dateTime(escrow.approvedAt) : "—"} />
            <DefRow
              label="Terminal state reached"
              value={escrow.terminalStateReachedAt ? fmt.dateTime(escrow.terminalStateReachedAt) : "—"}
            />
            <DefRow label="Archived at" value={escrow.archivedAt ? fmt.dateTime(escrow.archivedAt) : "—"} />
            <DefRow label="Last synced" value={escrow.lastSyncedAt ? fmt.dateTime(escrow.lastSyncedAt) : "—"} />
          </dl>
        </SectionCard>

        <SectionCard title="Webhook events">
          {escrow.events.length === 0 ? (
            <p className="text-small text-gray-mid">No events received yet.</p>
          ) : (
            <ol className="flex flex-col gap-space-2 m-0 p-0 list-none">
              {escrow.events.map((e) => (
                <li key={e.id} className="border-b border-border-light last:border-0 pb-space-2">
                  <div className="flex justify-between gap-space-2">
                    <span className="font-medium text-onyx">{e.event}</span>
                    <span className="text-caption text-gray-mid">{fmt.dateTime(e.receivedAt)}</span>
                  </div>
                  <details className="mt-space-1">
                    <summary className="text-caption text-gray-mid cursor-pointer">Payload</summary>
                    <pre className="text-caption overflow-auto whitespace-pre-wrap">{e.payloadJson}</pre>
                  </details>
                </li>
              ))}
            </ol>
          )}
        </SectionCard>
      </div>

      {escrow.edits.length > 0 && (
        <SectionCard title="Draft edit history">
          <ol className="flex flex-col gap-space-1 m-0 p-0 list-none">
            {escrow.edits.map((ed) => (
              <li key={ed.id} className="text-small text-onyx flex justify-between gap-space-3 border-b border-border-light last:border-0 py-space-1">
                <span>
                  <span className="font-medium">{ed.fieldName}</span>: {ed.oldValue ?? "—"} → {ed.newValue ?? "—"}
                </span>
                <span className="text-caption text-gray-mid">{fmt.dateTime(ed.editedAt)}</span>
              </li>
            ))}
          </ol>
        </SectionCard>
      )}

      {escrow.rawEscrowPayloadJson && canApprove && (
        <SectionCard title="Raw Escrow.com payload (admin only)">
          <details className="rounded-md bg-white-warm border border-border-light p-space-2">
            <summary className="text-caption text-gray-mid cursor-pointer">
              Show diagnostic payload
            </summary>
            <pre className="text-caption text-gray-mid overflow-auto whitespace-pre-wrap mt-space-2">
              {escrow.rawEscrowPayloadJson}
            </pre>
          </details>
        </SectionCard>
      )}

      <ApproveEscrowModal
        open={approveOpen}
        escrow={approveOpen ? escrow : null}
        onClose={() => setApproveOpen(false)}
        onApproved={() => void query.refetch()}
      />

      <Modal
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        title="Archive escrow transaction"
        description={escrow.dealNumber ?? undefined}
        footer={
          <>
            <Button variant="tertiary" onClick={() => setArchiveOpen(false)} disabled={archiveMutation.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => archiveMutation.mutate()} loading={archiveMutation.isPending}>
              Archive
            </Button>
          </>
        }
      >
        <p className="text-small text-gray-mid">
          Archived transactions are hidden from the default list — find them again with
          the “Show archived” filter. This does not affect the transaction on Escrow.com.
        </p>
      </Modal>
    </div>
  );
}
